import { observer } from 'mobx-react';
import { Card, Switch, Typography, message } from 'antd';
import { AudioOutlined, LoadingOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';

const { Text } = Typography;

// Interactive tile for the per-agent phrase-list master switch (EnablePhraseList).
// Mirrors SkillRouterTile: optimistic toggle, three-way state encoding (accent,
// pill text, switch), and a consequence-focused hint. When OFF, the agent biases
// STT toward NO vocabulary regardless of attached buckets — the master kill switch.
export const PhraseListEnabledTile = observer(function PhraseListEnabledTile() {
  const { agentDetailStore: detail, themeStore } = useStore();
  const agent = detail.agent;
  const isDark = themeStore.mode === 'dark';
  const enabled = !!agent?.enablePhraseList;
  const saving = detail.isSavingPhraseList;
  const bucketCount = detail.phraseGroups.length;

  const bg = enabled
    ? isDark
      ? `linear-gradient(135deg, ${colors.ink[700]}55 0%, ${colors.violet[700]}33 100%)`
      : `linear-gradient(135deg, ${colors.ink[50]} 0%, ${colors.violet[50]} 100%)`
    : isDark
      ? `linear-gradient(135deg, ${colors.slate[700]}55 0%, ${colors.slate[800]} 100%)`
      : `linear-gradient(135deg, ${colors.slate[50]} 0%, #FFFFFF 100%)`;

  const border = enabled
    ? isDark ? colors.ink[600] + '88' : colors.ink[200]
    : isDark ? colors.slate[700] : colors.slate[200];

  const accentColor = enabled
    ? isDark ? colors.ink[100] : colors.ink[600]
    : isDark ? colors.slate[400] : colors.slate[500];

  const labelColor = isDark ? colors.slate[400] : colors.slate[500];
  const headlineColor = isDark ? colors.slate[50] : colors.slate[900];
  const hintColor = isDark ? colors.slate[400] : colors.slate[500];

  const onToggle = async (next: boolean) => {
    try {
      await detail.setEnablePhraseList(next);
      message.success(next ? 'Phrase list enabled' : 'Phrase list disabled');
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Could not update phrase list');
    }
  };

  return (
    <Card
      styles={{
        body: {
          padding: 18,
          minHeight: 120,
          background: bg,
          border: `1px solid ${border}`,
          borderRadius: 12,
          position: 'relative',
          overflow: 'hidden',
          transition: 'background 200ms ease, border-color 200ms ease',
        },
      }}
      style={{ border: 'none', cursor: 'default' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <Text
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: labelColor,
          }}
        >
          Phrase list
        </Text>
        <AudioOutlined style={{ color: accentColor, opacity: enabled ? 0.9 : 0.4, fontSize: 18, lineHeight: 1, transition: 'opacity 200ms, color 200ms' }} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '14px 0 8px' }}>
        <Switch checked={enabled} onChange={onToggle} loading={saving} aria-label="Enable phrase list" />
        <span
          style={{
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: enabled ? accentColor : headlineColor,
            lineHeight: 1.1,
            transition: 'color 200ms',
          }}
        >
          {enabled ? 'Enabled' : 'Disabled'}
        </span>
        {saving && <LoadingOutlined aria-label="Saving" style={{ fontSize: 13, color: hintColor }} />}
      </div>

      <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.45, color: hintColor }}>
        {enabled
          ? bucketCount > 0
            ? `Biases voice transcription toward this agent's ${bucketCount} attached bucket${bucketCount === 1 ? '' : 's'}.`
            : 'On, but no buckets attached yet — add some in the Phrase Lists tab.'
          : 'Off — voice transcription gets no phrase hints, even if buckets are attached.'}
      </Text>
    </Card>
  );
});
